import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HelmetProvider } from 'react-helmet-async';
import axios from 'axios';
import './App.css';
import { Toaster } from 'react-hot-toast';

// Theme management
import { useTheme } from './hooks/useTheme';
// Google Analytics (loaded from database config)
import useGoogleAnalytics from './hooks/useGoogleAnalytics';
// Global loading indicator
import { LoadingProvider } from './context/LoadingContext';
import GlobalLoadingBar from './components/GlobalLoadingBar';
// Pages
import Login from './pages/Login';
import Signup from './pages/Signup';
import VerifyEmail from './pages/VerifyEmail';
import Dashboard from './pages/Dashboard';
import DashboardRouter from './pages/DashboardRouter';
import MenuManagement from './pages/MenuManagement';
import TableManagement from './pages/TableManagement';
import Orders from './pages/Orders';
import Inventory from './pages/Inventory';
import Loyalty from './pages/Loyalty';
import Staff from './pages/Staff';
// import Reports from './pages/Reports'; // Sales reports — hidden until sales functionality is available
import Analytics from './pages/Analytics';
import PublicMenu from './pages/PublicMenu';
import Settings from './pages/Settings';
import Privacy from './pages/Privacy';
import Landing from './pages/Landing';
import Layout from './components/Layout';
import Pricing from './pages/Pricing';
import Features from './pages/Features';
import About from './pages/About';
import Contact from './pages/Contact';
import Terms from './pages/Terms';
import Kvkk from './pages/Kvkk';
import Cookies from './pages/Cookies';
import Upgrade from './pages/Upgrade';
import Addons from './pages/Addons';
import Maintenance from './pages/Maintenance';
import Support from './pages/Support';

// Lazy-loaded System Admin pages
import {
  SystemDashboard,
  SystemSettings,
  SystemPricing,
  SystemTenants,
  SystemUsers,
  SystemSupport as SystemSupportPage,
  SystemAudit,
} from './pages/system';

// Multi-tenant context
import { TenantProvider } from './context/TenantContext';
import { DarkModeProvider } from './context/DarkModeContext';

// Intelligent API base URL resolution
const getBackendUrl = () => {
  // 1. Use environment variable if set
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  // 2. Use current domain + /api (for production deployed on same domain)
  const protocol = window.location.protocol;
  const host = window.location.host;
  return `${protocol}//${host}`;
};

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = React.createContext(null);

// Component to track route changes and update page title
function PageTitleUpdater() {
  const location = useLocation();

  useEffect(() => {
    // Map routes to page titles
    const routeToTitle = {
      '/admin': 'Dashboard',
      '/admin/menu': 'Menu Management',
      '/admin/tables': 'Table Management',
      '/admin/orders': 'Orders',
      '/admin/inventory': 'Inventory',
      '/admin/loyalty': 'Loyalty',
      '/admin/staff': 'Staff',
      // '/admin/reports': 'Reports',
      '/admin/analytics': 'Analytics',
      '/admin/settings': 'Settings',
      '/admin/addons': 'Add-ons',
      '/admin/system': 'System Settings',
      '/admin/system/pricing': 'System Pricing',
      '/admin/system/tenants': 'Tenant Management',
      '/admin/system/users': 'User Management',
      '/admin/system/support': 'Support Tickets',
      '/admin/system/audit': 'Audit Logs',
      '/menu': 'Public Menu',
      '/login': 'Login',
      '/signup': 'Signup',
      '/pricing': 'Pricing',
      '/features': 'Features',
      '/about': 'About',
      '/contact': 'Contact',
      '/terms': 'Terms',
      '/kvkk': 'KVKK',
      '/cookies': 'Cookies',
    };

    const path = location.pathname;
    let pageTitle = 'idemenu';
    
    // Check for exact match
    if (routeToTitle[path]) {
      pageTitle = `${routeToTitle[path]} - idemenu`;
    } 
    // Check for menu with tenantId
    else if (path.startsWith('/menu/')) {
      pageTitle = 'Public Menu - idemenu';
    }

    document.title = pageTitle;
  }, [location.pathname]);

  return null;
}

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const { i18n } = useTranslation();

  // Initialize theme on app startup
  useTheme();
  
  // Initialize Google Analytics from database config (loaded once globally)
  useGoogleAnalytics();
  
  // Setup axios interceptor to automatically include token in requests
  useEffect(() => {
    const interceptor = axios.interceptors.request.use(
      (config) => {
        const currentToken = localStorage.getItem('token');
        if (currentToken && !config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${currentToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Cleanup interceptor on unmount
    return () => axios.interceptors.request.eject(interceptor);
  }, []);

  useEffect(() => {
    if (token) {
      // Verify token and get user
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => setUser(res.data))
      .catch(() => {
        localStorage.removeItem('token');
        setToken(null);
      });
    }
  }, [token]);

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const authValue = { user, token, login, logout, API };

  // Maintenance mode - set to true before beta launch
  const MAINTENANCE_MODE = process.env.REACT_APP_MAINTENANCE_MODE === 'true';

  // If in maintenance mode, only show maintenance page (except for existing tenant menus and login)
  if (MAINTENANCE_MODE) {
    return (
      <HelmetProvider>
      <LoadingProvider>
      <DarkModeProvider>
        <AuthContext.Provider value={authValue}>
          <TenantProvider token={token}>
            <BrowserRouter>
              <GlobalLoadingBar />
              <Toaster position="top-center" />
              <Routes>
                {/* Allow public menu access even in maintenance mode */}
                <Route path="/menu" element={<PublicMenu />} />
                <Route path="/menu/:tenantId" element={<PublicMenu />} />
                {/* Allow login for existing users/admins */}
                <Route path="/login" element={!token ? <Login /> : <Navigate to="/" />} />
                {/* Protected routes for logged in users */}
                <Route
                  path="/"
                  element={token ? <Layout /> : <Maintenance />}
                >
                  <Route index element={<DashboardRouter />} />
                  <Route path="admin/menu" element={<MenuManagement />} />
                  <Route path="admin/upgrade" element={<Upgrade />} />
                  <Route path="admin/addons" element={<Addons />} />
                  <Route path="settings" element={<Settings />} />
                  {/* System Admin Routes - Lazy loaded, uses main Layout sidebar */}
                  <Route path="admin/system" element={<Navigate to="/" replace />} />
                  <Route path="admin/system/settings" element={<Suspense fallback={null}><SystemSettings /></Suspense>} />
                  <Route path="admin/system/pricing" element={<Suspense fallback={null}><SystemPricing /></Suspense>} />
                  <Route path="admin/system/tenants" element={<Suspense fallback={null}><SystemTenants /></Suspense>} />
                  <Route path="admin/system/users" element={<Suspense fallback={null}><SystemUsers /></Suspense>} />
                  <Route path="admin/system/support" element={<Suspense fallback={null}><SystemSupportPage /></Suspense>} />
                  <Route path="admin/system/audit" element={<Suspense fallback={null}><SystemAudit /></Suspense>} />
                  <Route path="admin/system/admin" element={<Navigate to="/" replace />} />
                </Route>
                {/* Everything else shows maintenance page */}
                <Route path="*" element={<Maintenance />} />
              </Routes>
            </BrowserRouter>
          </TenantProvider>
        </AuthContext.Provider>
      </DarkModeProvider>
      </LoadingProvider>
      </HelmetProvider>
    );
  }

  return (
    <HelmetProvider>
    <LoadingProvider>
    <DarkModeProvider>
      <AuthContext.Provider value={authValue}>
        <TenantProvider token={token}>
          <BrowserRouter>
            <GlobalLoadingBar />
            <Toaster position="top-center" />
            <PageTitleUpdater />
            <Routes>
            {/* Public Routes */}
            <Route path="/menu" element={<PublicMenu />} />
            <Route path="/menu/:tenantId" element={<PublicMenu />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/features" element={<Features />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/kvkk" element={<Kvkk />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/" element={<Landing />} />
            
            {/* Auth Routes */}
            <Route path="/login" element={!token ? <Login /> : <Navigate to="/admin" />} />
            <Route path="/signup" element={!token ? <Signup /> : <Navigate to="/" />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            
            {/* Landing Page */}
            <Route path="/" element={<Landing />} />
            
            {/* Admin Routes */}
            <Route path="/admin" element={token ? <Layout /> : <Navigate to="/login" />}>
              <Route index element={<DashboardRouter />} />
              <Route path="menu" element={<MenuManagement />} />
              <Route path="upgrade" element={<Upgrade />} />
              <Route path="addons" element={<Addons />} />
              <Route path="tables" element={<TableManagement />} />
              <Route path="orders" element={<Orders />} />
              <Route path="inventory" element={<Inventory />} />
              <Route path="loyalty" element={<Loyalty />} />
              <Route path="staff" element={<Staff />} />
              {/* <Route path="reports" element={<Reports />} /> */}
              <Route path="analytics" element={<Analytics />} />
              <Route path="settings" element={<Settings />} />
              <Route path="support" element={<Support />} />
              <Route path="tenants" element={<Navigate to="/admin/system/tenants" replace />} />
              {/* System Admin Routes - Lazy loaded, no nested layout (uses main Layout sidebar) */}
              <Route path="system" element={<Navigate to="/admin" replace />} />
              <Route path="system/settings" element={
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div></div>}>
                  <SystemSettings />
                </Suspense>
              } />
              <Route path="system/pricing" element={
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div></div>}>
                  <SystemPricing />
                </Suspense>
              } />
              <Route path="system/tenants" element={
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div></div>}>
                  <SystemTenants />
                </Suspense>
              } />
              <Route path="system/users" element={
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div></div>}>
                  <SystemUsers />
                </Suspense>
              } />
              <Route path="system/support" element={
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div></div>}>
                  <SystemSupportPage />
                </Suspense>
              } />
              <Route path="system/audit" element={
                <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div></div>}>
                  <SystemAudit />
                </Suspense>
              } />
              {/* Legacy redirect for old /admin/system/admin path */}
              <Route path="system/admin" element={<Navigate to="/admin/system" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </TenantProvider>
    </AuthContext.Provider>
    </DarkModeProvider>
  </LoadingProvider>
  </HelmetProvider>
  );
}

export default App;
